/**
 * The invite token's one hop across a sign-in round trip
 * (CR-SSO-INVITE-PX-2026-09-26, spec R.5.5).
 *
 * Two pages put the token here:
 *  - `/accept-invite` just before it leaves for the provider, so an SSO failure
 *    returns to the invite page it started from (read by `/auth/callback`);
 *  - the "sign in to accept" link (fix cycle 1b, INV-S9), so a password sign-in
 *    on `/login` comes back to the invite and accepts it signed in.
 *
 * sessionStorage keeps it to this tab and this session, and no HTTP request
 * ever carries it — every return is a client-side navigation. It expires after
 * {@link STASH_TTL_MS} and is cleared when a plain `/login` SSO flow starts
 * (fix cycle 1b, review low A6), so an abandoned invite start never sends a
 * later, unrelated sign-in back to an old invitation.
 *
 * Storage can be unavailable (private mode, blocked site data): every access is
 * guarded, and a failed read simply means there is no invite to return to.
 */

const KEY = 'px_sso_invite'

/** How long a stashed invite stays usable: one sign-in round trip, generously. */
export const STASH_TTL_MS = 30 * 60 * 1000

/**
 * Where the acceptance lands, from the invite's own validate answer. Neither
 * value is a secret and neither reaches a URL other than `/project/:projectId`
 * (TASK-7F3EE34E INV-I9).
 */
export interface InviteLanding {
  orgId?: string
  projectId?: string
}

/** Where an accepted invite lands: its project when it has one, else `/` (INV-I9). */
export const inviteLandingPath = ({ projectId }: InviteLanding): string =>
  projectId !== undefined ? `/project/${encodeURIComponent(projectId)}` : '/'

/** What the stash holds: the token plus the non-secret landing hints. */
export interface StashedInvite extends InviteLanding {
  token: string
}

interface Stashed extends StashedInvite {
  at: number
}

const optionalString = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined

const parse = (raw: string | null, now: number): StashedInvite | null => {
  if (raw === null) return null
  try {
    const value = JSON.parse(raw) as Partial<Stashed>
    if (typeof value.token !== 'string' || value.token === '' || typeof value.at !== 'number') return null
    if (now - value.at > STASH_TTL_MS) return null
    const orgId = optionalString(value.orgId)
    const projectId = optionalString(value.projectId)
    return {
      token: value.token,
      ...(orgId !== undefined ? { orgId } : {}),
      ...(projectId !== undefined ? { projectId } : {})
    }
  } catch {
    // A pre-TTL plain string, or anything else unreadable, counts as expired.
    return null
  }
}

/** Keep the invite token for the page the sign-in returns to. */
export function put(token: string, landing: InviteLanding = {}, now: number = Date.now()): void {
  try {
    const { orgId, projectId } = landing
    window.sessionStorage.setItem(
      KEY,
      JSON.stringify({
        token,
        at: now,
        ...(orgId !== undefined ? { orgId } : {}),
        ...(projectId !== undefined ? { projectId } : {})
      } satisfies Stashed)
    )
  } catch {
    // Unavailable storage: the return falls back to /login (or /).
  }
}

/** Read the kept invite and remove it in the same step; null when absent or expired. */
export function takeInvite(now: number = Date.now()): StashedInvite | null {
  try {
    const raw = window.sessionStorage.getItem(KEY)
    window.sessionStorage.removeItem(KEY)
    return parse(raw, now)
  } catch {
    return null
  }
}

/** {@link takeInvite}, token only; null when absent or expired. */
export function take(now: number = Date.now()): string | null {
  return takeInvite(now)?.token ?? null
}

/** Drop whatever is kept (a plain `/login` SSO start). */
export function clear(): void {
  try {
    window.sessionStorage.removeItem(KEY)
  } catch {
    // Unavailable storage holds nothing to clear.
  }
}

export const ssoInviteStash = { put, take, takeInvite, clear }
